/* ======================================================================
   Bundle Cart Display Override
   Shows tiered bundle pricing in cart instead of base Shopify prices
   ====================================================================== */

(function() {
  'use strict';

  // Format money helper
  function formatMoney(cents) {
    const amount = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }

  // A line is a subscription if Shopify says so. The _is_subscription property is
  // written by the builder at add time and is now always 'no', because the builder
  // adds one-time and the offer page converts the line afterwards via change.js —
  // which attaches a selling plan but cannot rewrite a line property. Trusting the
  // property alone priced converted lines at the one-time rate.
  function isSubscriptionLine(item) {
    if (!item) return false;
    if (item.selling_plan_allocation) return true;
    return !!(item.properties && item.properties._is_subscription === 'yes');
  }

  function isFreeGiftLine(item) {
    return !!(item && item.properties && item.properties._free_gift === 'yes');
  }

  // Which products are premixes, resolved once from the collection that defines
  // them. Everything else that needed this figure read it back from Liquid --
  // window.TLH_CART_JARS in <head>, or shipping-notice[data-bundle-jars] -- and
  // NEITHER re-renders on an AJAX cart update. Both go stale the moment a
  // customer changes a quantity, and a stale count is not merely a wrong number
  // here: it decides whether the free snack stays in the cart. Seen in both
  // directions -- a gift left behind after the cart dropped below the rung, and
  // a gift wrongly deleted because the page had been open since the cart was
  // empty. Counting from /cart.js against this set is the only figure that is
  // true at the moment it is used.
  let premixIds = null;
  let premixLoading = null;
  function loadPremixIds() {
    if (premixIds) return Promise.resolve(premixIds);
    if (premixLoading) return premixLoading;
    const handle = window.TLH_PREMIX_COLLECTION || 'pre-seasoned-lentils';
    premixLoading = fetch('/collections/' + handle + '/products.json?limit=250')
      .then(r => (r.ok ? r.json() : { products: [] }))
      .then(j => {
        premixIds = new Set((j.products || []).map(p => Number(p.id)));
        return premixIds;
      })
      .catch(() => { premixIds = new Set(); return premixIds; });
    return premixLoading;
  }

  function cartJarCount(cart) {
    if (!premixIds || !premixIds.size) return null;   // not resolved yet
    return cart.items.reduce(
      (n, i) => (premixIds.has(Number(i.product_id)) ? n + i.quantity : n),
      0
    );
  }

  // The gift is a real line that only the checkout discount makes free, so the
  // cart must not carry more of it than has been earned: the customer would be
  // charged for snacks the cart still calls FREE.
  //
  // Two ways that goes wrong. The cart falls back under the jar threshold, and
  // the gift has to go entirely. Or a subscription is cancelled back to one-time
  // after the offer page bumped the gift to two, and one has to go back. Both
  // are the same correction -- set the line to the quantity now earned, which
  // may be zero.
  //
  // Only ever downward. Adding is the builder's job at add-to-cart, and the
  // offer page's when a bundle is converted; doing it here would put items in
  // people's carts on page load.
  function earnedGiftQty(cart, jars) {
    if (!window.TLH_FREE_GIFT) return 0;
    const subscribed = cart.items.some(i => i.properties && i.properties._bundle_id && isSubscriptionLine(i));
    return window.TLH.giftQty(jars, subscribed);
  }

  function setCartAttributes(attributes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes }),
    });
  }

  // Bring the gift line into line with what the cart has earned, in BOTH
  // directions. It used to only ever remove, so a customer who dropped to five
  // jars and went back to six never saw the snack again -- not on the next
  // update, not on a refresh -- because adding was only ever done by the bundle
  // builder at add-to-cart.
  //
  // Adding back needs one thing removing did not: knowing whether the customer
  // WANTS it. Re-adding a snack somebody keeps deleting is worse than never
  // offering it, and somebody with an allergy would have no way to refuse
  // without dismantling their bundle. Two cart attributes carry that:
  //
  //   _gift_auto      a gift line is present because this code put it there
  //   _gift_declined  the customer took it out while still qualifying
  //
  // The customer removing the gift is only detectable as an absence, so the
  // pairing matters: if the line is gone while _gift_auto still says we put one
  // there, the only thing that can have removed it is the customer. Below the
  // rung we clear _gift_auto as we remove, so a later re-qualify reads as a
  // fresh earn rather than a refusal.
  //
  // Returns true when it changed the cart and a reload is coming.
  function reconcileGift(cart) {
    const gift = window.TLH_FREE_GIFT;
    if (!gift || !gift.variantId) return false;

    // Never act on a count we could not compute. Treating "unknown" as zero is
    // what deleted a gift the customer had legitimately earned.
    const jars = cartJarCount(cart);
    if (jars === null) return false;

    const line = cart.items.find(isFreeGiftLine);
    const earned = earnedGiftQty(cart, jars);
    const attrs = cart.attributes || {};
    const ours = attrs._gift_auto === 'yes';
    const declined = attrs._gift_declined === 'yes';

    // Too many, or none earned any more.
    if (line && line.quantity > earned) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: earned }),
      })
        .then(() => setCartAttributes({ _gift_auto: earned > 0 ? 'yes' : '' }))
        .then(() => window.location.reload())
        .catch(() => {});
      return true;
    }

    if (!line && earned > 0) {
      if (ours) {
        // We put one there and it is gone: the customer took it out. Remember
        // that and stop offering. No reload -- nothing on screen changes.
        setCartAttributes({ _gift_declined: 'yes', _gift_auto: '' }).catch(() => {});
        return false;
      }
      if (declined) return false;

      const bundleLine = cart.items.find(i => i.properties && i.properties._bundle_id);
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: gift.variantId,
            quantity: earned,
            properties: {
              '_free_gift': 'yes',
              '_gift_for': (bundleLine && bundleLine.properties._bundle_id) || '',
            },
          }],
        }),
      })
        .then(() => setCartAttributes({ _gift_auto: 'yes' }))
        .then(() => window.location.reload())
        .catch(() => {});
      return true;
    }

    return false;
  }

  // Mirrors CONFIG.getTieredPrice in assets/tlh-bundle-builder-v1.js. Two rates,
  // never stacked: the plan rate for a subscriber at any quantity, the holiday
  // rate for a one-time buyer at or above its threshold. Both percentages come
  // from the source-of-truth snippets, so this cannot drift from the builder.
  //
  // `quantity` is the bundle's jar count, not the line's -- the threshold is
  // about the size of the order, and every jar in a qualifying bundle is priced
  // at the same rate.
  function getTieredPrice(quantity, isSubscription) {
    if (isSubscription) {
      // Quantity-aware for the same reason the one-time side is: the
      // subscription ladder gained a second rung at 8+ packs.
      return window.TLH.subscriptionPrice(1300, quantity);
    }
    const offer = window.TLH_OFFER;
    if (offer && offer.enabled) {
      // Deepest rung first -- see the matching note in
      // assets/tlh-bundle-builder-v1.js. The cart and the builder must agree
      // on the price or the bundle appears to change value on its way here.
      if (offer.percent2 > 0 && quantity >= offer.minJars2) {
        return Math.round(1300 * (100 - offer.percent2) / 100);
      }
      if (offer.percent > 0 && quantity >= offer.minJars) {
        return Math.round(1300 * (100 - offer.percent) / 100);
      }
    }
    return 1300;
  }

  // Update cart display to show bundle prices
  function updateBundleDisplay() {
    // The premix set first: reconcileGift refuses to act without it, so
    // resolving it up front is what lets the very first pass decide correctly
    // instead of leaving an unearned gift until some later update.
    Promise.all([loadPremixIds(), fetch('/cart.js').then(r => r.json())])
      .then(([, cart]) => {
        // Before anything is painted: the gift line is brought into line with
        // what the cart has earned -- added, trimmed or removed -- and the page
        // reloaded, so nothing below runs against a stale cart.
        if (reconcileGift(cart)) return;

        // The gift is priced at zero whether or not a bundle survives beside it --
        // a cart can hold the snack and no jars only for the instant before the
        // reload above, but a cart of loose jars plus the gift is ordinary.
        cart.items.filter(isFreeGiftLine).forEach(item => updateCartItem(item, 0));

        // Find bundle items (items with _bundle_id property)
        const bundleItems = cart.items.filter(item =>
          item.properties && item.properties._bundle_id
        );

        if (bundleItems.length === 0) return;

        // Group items by bundle ID and calculate totals per bundle
        const bundles = {};
        bundleItems.forEach(item => {
          const bundleId = item.properties._bundle_id;
          if (!bundles[bundleId]) {
            bundles[bundleId] = {
              items: [],
              totalQuantity: 0,
              isSubscription: isSubscriptionLine(item)
            };
          }
          bundles[bundleId].items.push(item);
          bundles[bundleId].totalQuantity += item.quantity;
        });

        // Calculate total across all bundles with recalculated pricing
        let calculatedTotal = 0;
        let originalTotal = 0;

        Object.values(bundles).forEach(bundle => {
          // Recalculate price per jar based on CURRENT total quantity
          const pricePerJar = getTieredPrice(bundle.totalQuantity, bundle.isSubscription);

          bundle.items.forEach(item => {
            calculatedTotal += pricePerJar * item.quantity;
            originalTotal += (item.original_price || item.price) * item.quantity;
          });
        });

        // Check if any bundle is a subscription
        const isSubscription = bundleItems.some(isSubscriptionLine);

        console.log('Bundle cart display:', {
          calculatedTotal,
          originalTotal,
          isSubscription,
          itemCount: bundleItems.length,
          bundles: Object.keys(bundles).length
        });

        // Update each bundle item display with recalculated price
        Object.values(bundles).forEach(bundle => {
          const pricePerJar = getTieredPrice(bundle.totalQuantity, bundle.isSubscription);
          bundle.items.forEach(item => {
            updateCartItem(item, pricePerJar);
          });
        });

        // Anything in the cart that is not a bundle jar -- a snack, a jar added
        // from its own product page -- is priced by Shopify and passes straight
        // through. It has to be carried into the subtotal all the same: this
        // used to run only when the cart held nothing but bundle lines, so one
        // snack left the lines reading $11.05 each under a Total still quoting
        // Shopify's untouched figure.
        let passthroughTotal = 0;
        let passthroughOriginal = 0;
        // Tracked separately so the savings line can say where the money came
        // from. Without it the note credited the whole saving to the
        // subscription discount, and "$20.70" sitting under "15%" is arithmetic
        // that does not work -- 15% of this cart is $11.70; the other $9.00 is
        // the snack.
        let giftValue = 0;
        cart.items.forEach(item => {
          if (item.properties && item.properties._bundle_id) return;
          // The gift counts nothing towards what is owed and its full $9 towards
          // what was saved -- the same shape as a struck-through line.
          if (isFreeGiftLine(item)) {
            passthroughOriginal += item.original_line_price;
            giftValue += item.original_line_price;
            return;
          }
          passthroughTotal += item.final_line_price;
          passthroughOriginal += item.original_line_price;
        });

        updateCartSubtotal(
          calculatedTotal + passthroughTotal,
          isSubscription,
          bundleItems,
          passthroughOriginal,
          giftValue
        );

        // Add bundle messaging (use recalculated price for first bundle)
        const firstBundle = Object.values(bundles)[0];
        const firstBundlePrice = getTieredPrice(firstBundle.totalQuantity, firstBundle.isSubscription);
        addBundleMessaging(bundleItems.length, firstBundlePrice, isSubscription);
      })
      .catch(error => {
        console.error('Error updating bundle display:', error);
      });
  }

  // Update individual cart item display
  function updateCartItem(item, displayPrice) {
    // Try multiple selectors for different theme structures
    const selectors = [
      `[data-id="${item.key}"]`,  // For themes using data-id
      `.cart-form-item[data-line="${item.index + 1}"]`,  // For themes using cart-form-item (1-indexed)
      `[data-cart-item-key="${item.key}"]`,
      `[data-key="${item.key}"]`,
      `.cart-item[data-index="${item.index}"]`,
      `.cart__row[data-line-index="${item.index}"]`
    ];

    let itemElement = null;
    for (const selector of selectors) {
      itemElement = document.querySelector(selector);
      if (itemElement) break;
    }

    if (!itemElement) {
      console.warn('Could not find cart item element for:', item.key);
      return;
    }

    // Get original price - for subscriptions, use base price (1300) instead of discounted price
    const isSubscription = isSubscriptionLine(item);
    const originalPrice = isSubscription ? 1300 : (item.original_price || item.price);
    const showStrikethrough = originalPrice > displayPrice;

    const struck = (cents) =>
      `<span class="tlh-struck" style="text-decoration: line-through; color: #999; margin-right: 8px;">${formatMoney(cents)}</span>`;

    // "FREE", not "$0.00" — the gift is the one line whose figure is a claim
    // rather than an amount, and a row of zeroes reads like a pricing error.
    const isGift = isFreeGiftLine(item);
    const money = (cents) => (isGift ? 'FREE' : formatMoney(cents));

    const unitHTML = showStrikethrough
      ? struck(originalPrice) + `<span>${money(displayPrice)}</span>`
      : `<span>${money(displayPrice)}</span>`;

    // <span>/<strong>, matching the shape the Liquid renders: the mobile rules
    // in assets/tlh-cart.css suppress the Total cell's money by element
    // (`> strong`, `> span`, `> del`), and a bare text node slipped past them
    // and put the same figure on the row twice below 768px.
    const lineTotalHTML = showStrikethrough
      ? struck(originalPrice * item.quantity) +
        `<strong>${money(displayPrice * item.quantity)}</strong>`
      : `<strong>${money(displayPrice * item.quantity)}</strong>`;

    // Update line total
    const lineTotalSelectors = [
      '.cart-item__total',
      '.cart-item__line-price',
      '.cart__final-price',
      '[data-cart-item-line-price]'
    ];

    let lineTotalElement = null;
    for (const selector of lineTotalSelectors) {
      lineTotalElement = itemElement.querySelector(selector);
      if (lineTotalElement) break;
    }

    if (lineTotalElement) {
      lineTotalElement.innerHTML = lineTotalHTML;
    }

    // Update price display (try multiple selectors)
    const priceSelectors = [
      '.cart-item__price',
      '.cart__price',
      '[data-cart-item-price]',
      '.price'
    ];

    let priceElement = null;
    for (const selector of priceSelectors) {
      priceElement = itemElement.querySelector(selector);
      if (priceElement) break;
    }

    if (priceElement) {
      if (lineTotalElement) {
        // The /cart page. This slot holds the theme's two responsive halves:
        // the unit price for desktop, the line total for mobile, where the
        // Total column is dropped. They have to be rebuilt rather than flattened
        // -- writing one figure into the slot showed it at every width, which is
        // what put the same money on the row twice on desktop. Whether the unit
        // price shows at all is assets/tlh-cart.css's call, keyed off data-qty:
        // at quantity 1 it only repeats the Total column.
        priceElement.innerHTML =
          `<div class="element--hide-on-small">${unitHTML}</div>` +
          `<div class="element--hide-on-desk">${lineTotalHTML}</div>`;
      } else {
        // The drawer, which has no separate column: this slot is the line
        // total, the same figure the Liquid renders into it.
        priceElement.innerHTML = lineTotalHTML;
      }
    }

    // Add bundle badge if not already present. The gift says what it is instead
    // -- it is not part of the bundle it came with, and calling it a bundle item
    // would make the jars look like they were priced with a snack among them.
    if (!itemElement.querySelector('.tlh-bundle-badge')) {
      const badge = document.createElement('span');
      badge.className = 'tlh-bundle-badge';
      badge.textContent = isGift
        ? ((window.TLH_FREE_GIFT && window.TLH_FREE_GIFT.label) || 'Free gift')
        : 'Bundle Item';
      badge.style.cssText = `
        display: inline-block;
        background: ${isGift ? '#697825' : '#000'};
        color: #fff;
        padding: 2px 8px;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 600;
        margin: 4px 0;
      `;

      // Try to insert badge near product title. The last two selectors are this
      // theme's own, and only the gift is allowed to reach them: none of the
      // four above exist here, so "Bundle Item" has never actually rendered on
      // either cart, and switching it on now would put a black pill on every jar
      // in a row the bundle header already names.
      const titleSelectors = [
        '.cart-item__details',
        '.cart-item__name',
        '.cart__product-title',
        '.product-title'
      ].concat(isGift ? ['.cart-form-item__price-title-variants', '.cart-item__content > div'] : []);

      for (const selector of titleSelectors) {
        const titleElement = itemElement.querySelector(selector);
        if (titleElement) {
          // After the title, not before it: the price sits at the top of this
          // block on the /cart page, and a badge above that pushed the name off
          // the line the thumbnail is centred on.
          const title = titleElement.querySelector('.cart-item__title');
          if (title) title.insertAdjacentElement('afterend', badge);
          else titleElement.insertAdjacentElement('afterbegin', badge);
          break;
        }
      }
    }
  }

  // Update cart subtotal display
  function updateCartSubtotal(displayTotal, isSubscription, bundleItems, passthroughOriginal, giftValue) {
    const subtotalSelectors = [
      '.cart__total strong',
      '.cart__total .text-size--heading',
      '.cart-subtotal__price',
      '.cart__subtotal strong',
      '[data-cart-subtotal]',
      '.totals__subtotal-value'
    ];

    for (const selector of subtotalSelectors) {
      const subtotalElement = document.querySelector(selector);
      if (subtotalElement) {
        // Calculate original total - for subscriptions, use base price (1300) instead of discounted price
        let originalTotal = passthroughOriginal || 0;
        bundleItems.forEach(item => {
          const itemIsSubscription = isSubscriptionLine(item);
          const originalPrice = itemIsSubscription ? 1300 : (item.original_price || item.price);
          originalTotal += originalPrice * item.quantity;
        });

        // Show strikethrough if there's a discount
        if (originalTotal > displayTotal) {
          // Same class the line items use: assets/tlh-cart.css needs a hook here
          // to keep this span off the rule written for the "Total" label, which
          // was shrinking the was-price and pushing it onto a line of its own.
          subtotalElement.innerHTML = `<span class="tlh-struck" style="text-decoration: line-through; color: #999; margin-right: 8px;">${formatMoney(originalTotal)}</span>${formatMoney(displayTotal)}`;
        } else {
          subtotalElement.textContent = formatMoney(displayTotal);
        }

        // What they saved, in money. The panel shows $87.00 struck to $66.30
        // and nothing anywhere states the difference -- the customer has to do
        // the subtraction. The percentage is already on every line and in the
        // note below, so the AMOUNT is the only thing here that is new.
        //
        // Shown whenever anything was saved, not only on subscriptions: a
        // one-time cart that earned the free snack was striking $87.00 to
        // $78.00 with no explanation at all.
        const saved = originalTotal - displayTotal;
        if (saved > 0 && !document.querySelector('.tlh-cart-savings')) {
          const note = document.createElement('div');
          note.className = 'tlh-cart-savings';

          const amount = document.createElement('strong');
          amount.textContent = 'You save ' + formatMoney(saved);
          note.appendChild(amount);

          // Where the money came from. Both sources are named when both are in
          // play, because crediting it all to the percentage makes the figure
          // above unverifiable: 15% of this cart is $11.70, and the rest is the
          // snack. With no gift the whole saving IS the percentage, so the
          // shorter sentence is the true one.
          var pct = (window.TLH_SELLING_PLAN && window.TLH_SELLING_PLAN.discountPercent) || null;
          const sources = [];
          if (isSubscription) sources.push(pct ? pct + '% subscription discount' : 'Subscription discount');
          if (giftValue > 0) sources.push('your free snack');

          if (sources.length) {
            const why = document.createElement('span');
            why.textContent = sources.join(' + ');
            note.appendChild(why);
          }

          // Styled by assets/tlh-cart.css and assets/tlh-cart-drawer.css, not
          // here. It used to carry an inline cssText -- a pale yellow box with
          // an orange bar and #222 system text -- which was the only element in
          // either cart that ignored the theme's palette and typeface.
          subtotalElement.parentElement.insertAdjacentElement('afterend', note);
        }
        break;
      }
    }
  }

  // Add bundle messaging
  function addBundleMessaging(itemCount, pricePerJar, isSubscription) {
    // Don't add if already exists
    if (document.querySelector('.tlh-bundle-pricing-info')) return;

    const cartTotalsSelectors = [
      '.cart__footer',
      '.cart-footer',
      '.totals',
      '.cart__bottom'
    ];

    let cartFooter = null;
    for (const selector of cartTotalsSelectors) {
      cartFooter = document.querySelector(selector);
      if (cartFooter) break;
    }

    if (!cartFooter) return;

    // Create pricing info message
    const message = document.createElement('div');
    message.className = 'tlh-bundle-pricing-info';

    let messageText = '';
    const priceDisplay = formatMoney(pricePerJar);

    // Rungs read from the source-of-truth snippets rather than being spelled out
    // here, and the subscription percentage too -- this line used to say "15%
    // off" in a string literal, which is exactly the kind of copy that is still
    // saying 15 the day after the plan changes.
    const shipMin = (window.TLH_OFFER && window.TLH_OFFER.shippingMinJars) || 5;
    const giftQty = window.TLH.giftQty(itemCount, isSubscription);

    const earned = [];
    if (itemCount >= shipMin) earned.push('FREE shipping');
    if (giftQty > 0) earned.push(giftQty > 1 ? `${giftQty} free snacks` : 'a free snack');

    if (isSubscription) {
      const pct = (window.TLH_SELLING_PLAN && window.TLH_SELLING_PLAN.discountPercent) || 15;
      messageText = `✓ <strong>Subscription Bundle</strong> - ${itemCount} packs at ${priceDisplay} each (${pct}% off)`;
      if (earned.length) messageText += ` + ${earned.join(' and ')}`;
    } else if (earned.length) {
      messageText = `✓ <strong>${itemCount} packs</strong> at ${priceDisplay} each + ${earned.join(' and ')}`;
    } else {
      messageText = `${itemCount} pack${itemCount > 1 ? 's' : ''} at ${priceDisplay} each`;
    }

    message.innerHTML = messageText;
    message.style.cssText = `
      background: #f0f9ff;
      border-left: 4px solid #0ea5e9;
      padding: 12px;
      margin: 12px 0;
      font-size: 14px;
      color: #222;
      border-radius: 4px;
    `;

    // The note belongs wherever a discount is actually in play, which is now the
    // holiday threshold rather than the retired 6-jar bulk tier.
    const offerMin = (window.TLH_OFFER && window.TLH_OFFER.enabled && window.TLH_OFFER.minJars) || 0;
    if (offerMin > 0 && itemCount >= offerMin && !isSubscription) {
      const checkoutNote = document.createElement('div');
      checkoutNote.className = 'tlh-checkout-discount-note';
      checkoutNote.innerHTML = '💡 Bundle discount applies automatically at checkout';
      checkoutNote.style.cssText = `
        background: #fffbe9;
        border-left: 4px solid #f4a300;
        padding: 12px;
        margin-top: 8px;
        font-size: 13px;
        color: #666;
        border-radius: 4px;
      `;
      message.appendChild(checkoutNote);
    }

    cartFooter.insertAdjacentElement('afterbegin', message);
  }

  // A fingerprint of the cart as LIQUID last rendered it. Every part of it is
  // server-rendered and none of it is ever written by this file, which is what
  // lets the observer below tell a real cart change from its own echo.
  function domCartFingerprint() {
    const total = document.querySelector('[data-cart-total]');
    const count = document.querySelector('[data-cart-count]');
    const jars = document.querySelector('[data-bundle-jars]');
    return [
      total ? total.textContent.trim() : '',
      count ? count.textContent.trim() : '',
      jars ? jars.getAttribute('data-bundle-jars') : '',
    ].join('|');
  }

  // Debounced wrapper — collapses multiple rapid calls into one.
  //
  // This used to hold a one-second lock and DROP anything that arrived inside
  // it. The lock was there because the observer fired on this file's own writes
  // and would otherwise loop -- but it also swallowed genuine cart changes, so
  // removing a jar left the summary showing the old total until a page refresh.
  // The observer now filters its own echo by fingerprint instead, so nothing
  // has to be dropped and a plain debounce is enough.
  let debounceTimer = null;
  function debouncedUpdate() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(updateBundleDisplay, 200);
  }

  // Initialize
  function init() {
    debouncedUpdate();
  }

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Listen for cart updates
  document.addEventListener('cart:updated', debouncedUpdate);
  document.addEventListener('cart-updated', debouncedUpdate);

  // Also listen for custom Shopify cart events
  if (window.Shopify && window.Shopify.onCartUpdate) {
    const originalOnCartUpdate = window.Shopify.onCartUpdate;
    window.Shopify.onCartUpdate = function() {
      originalOnCartUpdate.apply(this, arguments);
      debouncedUpdate();
    };
  }

  // Watch for cart re-renders (the theme replaces #AjaxCartSubtotal wholesale).
  //
  // Only act when the SERVER-rendered figures actually changed. This file writes
  // into .cart__total strong and the line prices, which sit inside the observed
  // subtree, so an unfiltered observer fired on its own output -- the reason the
  // debounce used to need a lock that dropped real updates.
  const observeCartDrawer = () => {
    const cartSubtotal = document.getElementById('AjaxCartSubtotal');
    if (!cartSubtotal) return;
    let lastFingerprint = domCartFingerprint();
    const observer = new MutationObserver(() => {
      const fingerprint = domCartFingerprint();
      if (fingerprint === lastFingerprint) return;
      lastFingerprint = fingerprint;
      debouncedUpdate();
    });
    observer.observe(cartSubtotal, { childList: true, subtree: true, attributes: true });
  };

  // Start observing after page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCartDrawer);
  } else {
    observeCartDrawer();
  }

  // Expose update function globally for themes that need it
  window.updateBundleCartDisplay = debouncedUpdate;
})();
